import jsonLogic, { type RulesLogic } from "json-logic-js";
import type { Condition, UiSchema, UiStep } from "./types";
import { pathsInStep } from "./schema";

let extended = false;

/** Registers NCP-specific JsonLogic operations once (idempotent). Shared with the rule engine on the server (same names). */
export function ensureLogicOperations(): void {
  if (extended) return;
  extended = true;
  jsonLogic.add_operation("count", (v: unknown) => (Array.isArray(v) ? v.length : v && typeof v === "object" ? Object.keys(v).length : 0));
  jsonLogic.add_operation("sum", (arr: unknown, field?: string) => (Array.isArray(arr) ? arr.reduce<number>((acc, x) => acc + (Number(field ? (x as Record<string, unknown>)?.[field] : x) || 0), 0) : 0));
  jsonLogic.add_operation("isEmpty", (v: unknown) => v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0) || (typeof v === "object" && v !== null && Object.keys(v as object).length === 0));
  jsonLogic.add_operation("daysBetween", (a: unknown, b: unknown) => {
    const da = a ? new Date(String(a)).getTime() : NaN;
    const db = b ? new Date(String(b)).getTime() : Date.now();
    return Number.isNaN(da) || Number.isNaN(db) ? null : Math.round((db - da) / 86_400_000);
  });
  jsonLogic.add_operation("today", () => new Date().toISOString().slice(0, 10));
  jsonLogic.add_operation("matches", (v: unknown, pattern: unknown) => {
    try {
      return new RegExp(String(pattern)).test(String(v ?? ""));
    } catch {
      return false;
    }
  });
  jsonLogic.add_operation("lower", (v: unknown) => String(v ?? "").toLowerCase());
  jsonLogic.add_operation("any", (arr: unknown, field: string, value: unknown) => Array.isArray(arr) && arr.some((x) => (x as Record<string, unknown>)?.[field] === value));
  jsonLogic.add_operation("all", (arr: unknown, field: string, value: unknown) => Array.isArray(arr) && arr.length > 0 && arr.every((x) => (x as Record<string, unknown>)?.[field] === value));
}

/** Evaluates a condition against the submission. Missing / undefined conditions are `true`; evaluation errors are `false`. */
export function evaluate(condition: Condition | undefined, data: unknown): boolean {
  if (condition === undefined || condition === null) return true;
  if (typeof condition === "boolean") return condition;
  ensureLogicOperations();
  try {
    return jsonLogic.truthy(jsonLogic.apply(condition as unknown as RulesLogic, data ?? {}));
  } catch {
    return false;
  }
}

/** Evaluates a JsonLogic expression returning its raw result (for computed values). */
export function compute(expression: Condition, data: unknown): unknown {
  ensureLogicOperations();
  try {
    return jsonLogic.apply(expression as unknown as RulesLogic, data ?? {});
  } catch {
    return undefined;
  }
}

export interface VisibilityMap {
  steps: Set<string>;
  sections: Set<string>;
  fields: Set<string>;
  required: Set<string>;
  disabled: Set<string>;
}

/** Computes which steps, sections and fields are visible / required / disabled for the given data. */
export function visibility(ui: UiSchema, data: unknown): VisibilityMap {
  const steps = new Set<string>();
  const sections = new Set<string>();
  const fields = new Set<string>();
  const required = new Set<string>();
  const disabled = new Set<string>();
  for (const step of ui.steps) {
    if (!evaluate(step.visibleWhen, data)) continue;
    steps.add(step.key);
    for (const section of step.sections) {
      if (!evaluate(section.visibleWhen, data)) continue;
      sections.add(`${step.key}/${section.key}`);
      for (const f of section.fields) {
        if (f.content) continue;
        if (!evaluate(f.visibleWhen, data)) continue;
        fields.add(f.path);
        if (f.requiredWhen !== undefined && evaluate(f.requiredWhen, data)) required.add(f.path);
        if (f.disabledWhen !== undefined && evaluate(f.disabledWhen, data)) disabled.add(f.path);
      }
    }
  }
  return { steps, sections, fields, required, disabled };
}

/** Visible steps in order. */
export function visibleSteps(ui: UiSchema, data: unknown): UiStep[] {
  return ui.steps.filter((s) => evaluate(s.visibleWhen, data));
}

/** Field paths of a step that are currently visible. */
export function visiblePathsInStep(step: UiStep, data: unknown): string[] {
  const all = new Set(pathsInStep(step));
  const out: string[] = [];
  for (const section of step.sections) {
    if (!evaluate(section.visibleWhen, data)) continue;
    for (const f of section.fields) if (!f.content && all.has(f.path) && evaluate(f.visibleWhen, data)) out.push(f.path);
  }
  return out;
}

/** Collects `{ var }` references used by a condition (for the builder's dependency hints). */
export function referencedVars(condition: Condition | undefined, out: Set<string> = new Set()): Set<string> {
  if (!condition || typeof condition !== "object") return out;
  if (Array.isArray(condition)) {
    condition.forEach((c) => referencedVars(c as Condition, out));
    return out;
  }
  for (const [op, arg] of Object.entries(condition as Record<string, unknown>)) {
    if (op === "var") {
      const name = Array.isArray(arg) ? arg[0] : arg;
      if (typeof name === "string" && name) out.add(name);
    } else referencedVars(arg as Condition, out);
  }
  return out;
}

/** Wrapper so hosts can add domain operations before rendering. */
export function addLogicOperation(name: string, fn: (...args: unknown[]) => unknown): void {
  ensureLogicOperations();
  jsonLogic.add_operation(name, fn as (...args: never[]) => unknown);
}
